/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { injectable } from 'inversify'
import { IRequestHistoryApiClient } from './contracts'
import axios from 'axios'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IUserSessionMiddleware } from '../../arc/middleware/UserSessionMiddleware/contracts'
import { SettingsManager } from '../../configuration/SettingsManager'
import { ICoverageDiscoveryRequestHistoryGridCriteria, ICoverageDiscoveryRequestHistoryRequestObject, ICoverageDiscoveryRequestHistoryGridResults, IReviewedData } from '../RequestHistoryStateManager/contracts'
import { ErrorHelper } from '../../utils/ErrorHelper'
import axiosRetry from 'axios-retry'

@injectable()
export class RequestHistoryApiClient implements IRequestHistoryApiClient {
	@injectProperty('IUserSessionMiddleware')
	private readonly userSessionMiddleware!: IUserSessionMiddleware

	list(batchRunId: number, criteria: ICoverageDiscoveryRequestHistoryGridCriteria): Promise<ICoverageDiscoveryRequestHistoryGridResults> {
		// TODO: the %2b encodes the plus '+' sign such that .net api doesnt strip it out from reaching the controller. Clean this up
		const filterString = ''
		const filterRequest: ICoverageDiscoveryRequestHistoryRequestObject = {
			duration: '',
			firstName: '',
			lastName: '',
			limit: 0,
			offset: 0,
			sort: '',
			ssn: '',
			transactionStatuses: null,
			eligibilityStatuses: null,
			dob: null,
			npis: null,
			payers: null,
			runDate: null,
			transactionDate: null,
		}
		if (criteria.searchTerm.firstName) {
			filterRequest.firstName = criteria.searchTerm.firstName
		}
		if (criteria.searchTerm.lastName) {
			filterRequest.lastName = criteria.searchTerm.lastName
		}
		if (criteria.searchTerm.ssn) {
			filterRequest.ssn = criteria.searchTerm.ssn.replace(/-/g, '')
		}
		if (criteria.searchTerm.dob) {
			filterRequest.dob = criteria.searchTerm.dob.toDateString()
		}
		if (criteria.searchTerm.npis) {
			if (criteria.searchTerm.npis.length !== 0) {
				const ids = criteria.searchTerm.npis.map(item => item.id)
				filterRequest.npis = ids.toString()
			}
		}

		if (criteria.searchTerm.payers) {
			if (criteria.searchTerm.payers.length !== 0) {
				const ids = criteria.searchTerm.payers.map(item => item.id)
				filterRequest.payers = ids.toString()
			}
		}

		if (criteria.searchTerm.transactionStatus) {
			if (criteria.searchTerm.transactionStatus.length !== 0) {
				const ids = criteria.searchTerm.transactionStatus.map(item => item.id)
				filterRequest.transactionStatuses = ids.toString()
			}
		}

		if (criteria.searchTerm.eligibilityStatus) {
			if (criteria.searchTerm.eligibilityStatus.length !== 0) {
				const ids = criteria.searchTerm.eligibilityStatus.map(item => item.id)
				filterRequest.eligibilityStatuses = ids.toString()
			}
		}

		if (criteria.searchTerm.runDate) {
			filterRequest.transactionDate = criteria.searchTerm.runDate.toDateString()
			filterRequest.duration = criteria.dateRange != null ? criteria.dateRange.toString() : undefined
		}

		const dateTimeStamp = `t=${Date.now()}`
		const sortField = criteria.sortField.charAt(0).toUpperCase() + criteria.sortField.slice(1)
		const sort = `${criteria.sortOrder === 'asc' ? '+' : '-'}${sortField}`
		criteria.page = criteria.page > 0 ? criteria.page : 1
		const offset = (criteria.page - 1) * criteria.sizePerPage
		const limit = criteria.sizePerPage
		filterRequest.offset = offset
		filterRequest.limit = limit
		filterRequest.sort = sort
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/coveragediscoverytransactions/RequestHistoryList/${batchRunId}`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token => {
				axiosRetry(axios, {
					retries: 1,
					retryCondition: (error: any) => error && error.response && error.response.status === 401,
				})
				return axios.post(url, filterRequest, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
			})
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	updateReviewed(reviewedData: IReviewedData): Promise<any> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/coveragediscoverytransactions/updateReviewedData`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.post(url, reviewedData, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}
}
